import FileUploaderV2 from '@core/components/ApplyDialogV2/FileUploaderV2'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import type { FileError } from '@core/app/types'
import React, { useState, useRef } from 'react'
import { InfoIcon } from '@core/assets/icons/info_icon'
import './ApplyV2CvStep.scss'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import { usePostCvV2ApplyMutation, useLazyGetApplyV2SocketQuery } from '@core/app/api/applyJobV2Api'
import { makeFakeFileFromApi } from '@core/utils/helpers/fileApiHelpers';
import type { ApplyV2SettingsSectionName } from '@core/app/types/applyV2Type'

interface ApplyV2CvStepProps {
    navItems: {
        name: ApplyV2SettingsSectionName
        visible: boolean
        required: boolean
        order: number
    }[]
    currentStep: ApplyV2SettingsSectionName | null
}

const ApplyV2CvStep = ({ navItems, currentStep }: ApplyV2CvStepProps) => {
    const [files, setFiles] = useState<File[]>([])
    const [fileErrors, setFileErrors] = useState<FileError[]>([])
    const [loading, setLoading] = useState(false)
    const { initData, onSelectStep } = useApplyV2Context();
    const [postCv] = usePostCvV2ApplyMutation();
    const [trigger, { data, isFetching, isError }] = useLazyGetApplyV2SocketQuery();
    const wsRef = useRef<WebSocket | null>(null)


    const getNextStep = () => {
        const sorted = navItems
            .filter(item => item.visible)
            .sort((a, b) => a.order - b.order);
        const currentIdx = sorted.findIndex(item => item.name === currentStep);
        return sorted[currentIdx + 1]?.name || null;
    };

    const handleSkip = async () => {
        const nextStep = getNextStep();
        if (nextStep) {
            onSelectStep(nextStep);
        }
    };

    const cvFileFromApi = initData?.data?.cv ? makeFakeFileFromApi(initData.data.cv) : null;

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split('base64,')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleNext = async () => {
        if (!files.length) {
            // nextStep();
            return;
        }
        setLoading(true);
        try {
            const file = files[0];
            const base64 = await fileToBase64(file);
            const ext = file.name.split('.').pop() || '';
            const body = {
                cv: {
                    name: file.name,
                    ext,
                    base64,
                },
            };
            const uid = initData?.data?.uid;
            if (!uid) throw new Error('No UID');
            await postCv({ uid, body }).unwrap();

            const result = await trigger({ uid });
            console.log('Socket data:', result.data);
            const authUrl = result.data?.data?.authUrl
            if (!authUrl) throw new Error('No authUrl from WS')

            const ws = new window.WebSocket(authUrl)
            wsRef.current = ws

            ws.onopen = () => {

                ws.send(JSON.stringify({ action: "subscribe" }))

                ws.send(JSON.stringify({ action: "cv-parser" }))
            }

            ws.onmessage = (e) => {
                try {
                    const data = JSON.parse(e.data)
                    console.log('WS Message:', data)
                } catch (err) {
                    console.log('Raw WS:', e.data)
                }
            }

            ws.onerror = (e) => {
                console.error('WebSocket error:', e)
            }

            ws.onclose = (e) => {
                console.warn('WebSocket closed:', e)
            }

            handleSkip();

        } catch (e) {
            console.log('here is error')
            console.log(e)

            setFileErrors([
                {
                    file: files[0],
                    error: 'Error uploading the file. Please try again.',
                },
            ]);
        } finally {
            setLoading(false);
        }

    }

    return (
        <div style={{ width: '100%' }}>
            {files.length === 0 && !cvFileFromApi && (
                <>
                    <FileUploaderV2 onChange={setFiles} onError={setFileErrors} />

                    <div className="ApplyV2CvStep__info">
                        <span className="ApplyV2CvStep__info-icon">
                            <InfoIcon />
                        </span>
                        <span>
                            Information from your CV file can be automatically parsed to fill in the details in the following steps.
                        </span>
                    </div>
                </>
            )}
            {files.length === 0 && cvFileFromApi && (
                <FileUploaderV2List
                    files={[cvFileFromApi]}
                    fileErrors={fileErrors}
                    onDelete={undefined}
                    oldCv={true}
                />
            )}
            <FileUploaderV2List
                files={files}
                fileErrors={fileErrors}
                onDelete={(file) => setFiles(files.filter(f => f !== file))}
            />
            <div className="ApplyV2CvStep__actions">
                <button className="ApplyV2CvStep__skip-btn" type="button" onClick={handleSkip}>
                    Skip
                </button>
                <button
                    className="ApplyV2CvStep__next-btn"
                    type="button"
                    onClick={handleNext}
                    disabled={
                        files.length === 0 ||
                        fileErrors.length > 0
                    }
                >
                    {'Next'}
                </button>
            </div>
        </div>
    )
}

export default ApplyV2CvStep
